import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Promotion {
  id: string;
  provider_id: string;
  service_id: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'amount';
  discount_percentage?: number;
  discount_amount?: number;
  start_date: string;
  end_date: string;
  days_of_week: number[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  services?: {
    name: string;
  } | null;
}

export interface CreatePromotionData {
  provider_id: string;
  service_id: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'amount';
  discount_percentage?: number;
  discount_amount?: number;
  start_date: string;
  end_date: string;
  days_of_week: number[];
  is_active?: boolean;
}

export const usePromotions = (providerId?: string) => {
  return useQuery({
    queryKey: ['promotions', providerId],
    queryFn: async () => {
      if (!providerId) throw new Error('Provider ID is required');
      
      console.log('Fetching promotions for provider:', providerId);
      
      // First, let's just get promotions without the join to see if that works
      const { data: promotionsData, error: promotionsError } = await supabase
        .from('promotions')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (promotionsError) {
        console.error('Error fetching promotions:', promotionsError);
        throw promotionsError;
      }

      console.log('Raw promotions data:', promotionsData);

      // If we have promotions, let's get the service names separately
      if (promotionsData && promotionsData.length > 0) {
        const serviceIds = [...new Set(promotionsData.map(p => p.service_id))];
        
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('id, name')
          .in('id', serviceIds);

        if (servicesError) {
          console.error('Error fetching services:', servicesError);
          // Continue without service names rather than failing completely
        }

        // Map service names to promotions
        const promotionsWithServices = promotionsData.map(promotion => ({
          ...promotion,
          services: servicesData?.find(service => service.id === promotion.service_id) 
            ? { name: servicesData.find(service => service.id === promotion.service_id)!.name }
            : null
        }));

        console.log('Promotions with services:', promotionsWithServices);
        return promotionsWithServices as Promotion[];
      }

      return promotionsData as Promotion[] || [];
    },
    enabled: !!providerId,
  });
};

export const useActivePromotions = (serviceId?: string, bookingDate?: string) => {
  return useQuery({
    queryKey: ['active-promotions', serviceId, bookingDate],
    queryFn: async () => {
      if (!serviceId) return [];
      
      console.log('Fetching active promotions for service:', serviceId, 'date:', bookingDate);
      
      const { data, error } = await supabase
        .from('promotions')
        .select('*')
        .eq('service_id', serviceId)
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString().split('T')[0])
        .gte('end_date', new Date().toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching active promotions:', error);
        throw error;
      }
      
      // Filter by day of week if bookingDate is provided
      let filteredPromotions = data || [];
      if (bookingDate) {
        // Parse date avoiding timezone issues by using the date components directly
        const dateParts = bookingDate.split('-').map(part => parseInt(part, 10));
        const selectedDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]); // year, month (0-indexed), day
        const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        filteredPromotions = (data || []).filter(promotion => {
          const isValidDay = promotion.days_of_week.includes(dayOfWeek);
          console.log('Promotion day check:', {
            bookingDate,
            selectedDateString: selectedDate.toDateString(),
            dayOfWeek,
            dayName: dayNames[dayOfWeek],
            promotionName: promotion.name,
            validDays: promotion.days_of_week,
            validDayNames: promotion.days_of_week.map(d => dayNames[d]),
            isValidDay
          });
          return isValidDay;
        });
      }
      
      console.log('Active promotions after day filtering:', filteredPromotions);
      return filteredPromotions as Promotion[];
    },
    enabled: !!serviceId,
  });
};

export const useCreatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePromotionData) => {
      console.log('usePromotions received data:', data);
      
      // Prepare the data for insertion, ensuring only the appropriate discount field is set
      const insertData = {
        provider_id: data.provider_id,
        service_id: data.service_id,
        name: data.name,
        description: data.description,
        discount_type: data.discount_type,
        discount_percentage: data.discount_type === 'percentage' ? data.discount_percentage : null,
        discount_amount: data.discount_type === 'amount' ? data.discount_amount : null,
        start_date: data.start_date,
        end_date: data.end_date,
        days_of_week: data.days_of_week,
        is_active: data.is_active ?? true,
      };

      console.log('Final insertData for database:', insertData);
      console.log('Database insert data types:', {
        discount_type: typeof insertData.discount_type,
        discount_percentage: typeof insertData.discount_percentage,
        discount_amount: typeof insertData.discount_amount,
        discount_percentage_value: insertData.discount_percentage,
        discount_amount_value: insertData.discount_amount
      });

      const { data: promotion, error } = await supabase
        .from('promotions')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Database insertion error:', error);
        throw error;
      }
      return promotion;
    },
    onSuccess: (_, variables) => {
      toast.success('Promotion created successfully');
      queryClient.invalidateQueries({ queryKey: ['promotions', variables.provider_id] });
    },
    onError: (error) => {
      console.error('Error creating promotion:', error);
      toast.error('Failed to create promotion');
    },
  });
};

export const useUpdatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Promotion> & { id: string }) => {
      // Prepare the data for update, ensuring only the appropriate discount field is set
      const updateData = {
        ...data,
        discount_percentage: data.discount_type === 'percentage' ? data.discount_percentage : null,
        discount_amount: data.discount_type === 'amount' ? data.discount_amount : null,
      };

      const { data: promotion, error } = await supabase
        .from('promotions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return promotion;
    },
    onSuccess: (promotion) => {
      toast.success('Promotion updated successfully');
      queryClient.invalidateQueries({ queryKey: ['promotions', promotion.provider_id] });
    },
    onError: (error) => {
      console.error('Error updating promotion:', error);
      toast.error('Failed to update promotion');
    },
  });
};

export const useDeletePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('promotions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Promotion deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
    onError: (error) => {
      console.error('Error deleting promotion:', error);
      toast.error('Failed to delete promotion');
    },
  });
};
